import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// @mui
import { Box, Drawer, Stack } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
// hooks
import useCollapseDrawer from "../../../hooks/useCollapseDrawer";
import useResponsive from "../../../hooks/useResponsive";
import useAuth from "../../../hooks/useAuth";
// utils
import cssStyles from "../../../utils/cssStyles";
// config
import { NAVBAR } from "../../../config";
// components
import { NavSectionVertical } from "~components/nav-section";
import Scrollbar from "~components/Scrollbar";
//
import Logo from "~components/Logo";
import LogoFull from "~src/components/LogoFull";
import CollapseButton from "./CollapseButton";
import { NavbarFooter } from "./NavbarFooter";
import getNavConfig from "./NavConfig";
import { SearchBar } from "./SearchBar";
import { PATH_SEARCH } from "~src/routes/paths";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({
  isOpenSidebar,
  onCloseSidebar,
}: Props) {
  const theme = useTheme();
  const { user } = useAuth();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isDesktop = useResponsive("up", "lg");

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        maxHeight: "calc(100vh - 25px)",
        overflow: "auto",
        "& .simplebar-content": {
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: "center" }),
          maxHeight: "calc(100vh - 64px)",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between"
          }}>
          {!isCollapse && (
            <Stack direction={"row"}>
              <LogoFull />
            </Stack>
          )}

          {isCollapse && <Logo width={32} height={32} />}

          {isDesktop && !isCollapse && (
            <CollapseButton
              onToggleCollapse={onToggleCollapse}
              collapseClick={collapseClick}
            />
          )}
        </Stack>
      </Stack>
      {!isCollapse && (
        <Box sx={{ px: 3, mb: 1 }}>
          <SearchBar
            onSearch={(searchTerm) => {
              navigate(PATH_SEARCH.result(searchTerm || ""));
            }}
          />
        </Box>
      )}
      <NavSectionVertical navConfig={getNavConfig(user)} isCollapse={isCollapse} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
          <NavbarFooter isCollapse={isCollapse} />
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              height: "100vh",
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: "solid",
              borderRightColor: theme.palette.grey[200],
              borderRightWidth: 1,
              bgcolor: "background.default",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
          <NavbarFooter isCollapse={isCollapse} />
        </Drawer>
      )}
    </RootStyle>
  );
}
